import "mapbox-gl/dist/mapbox-gl.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Map, { Marker, NavigationControl, type MapRef, type ViewStateChangeEvent } from "react-map-gl/mapbox";
import "./styles.css";

const MAP_STYLE = "mapbox://styles/mapbox/streets-v12";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MapPoint {
    lat: number;
    lng: number;
    title?: string;
    icon?: string;
    color?: "orange" | "green" | "red" | "gray";
}

interface MainMapProps {
    /** Initial center latitude */
    lat?: number;
    /** Initial center longitude */
    lng?: number;
    /** Initial zoom level */
    zoom?: number;
    /** Array of points to display as markers */
    points?: MapPoint[];
    /** Input mode: 0 = view only, 1 = single marker, >1 = multiple markers (up to this count) */
    input?: number;
    /** Callback when markers change (in input mode) */
    onChange?: (markers: MapPoint[]) => void;
    /** Map height (default: 400px) */
    height?: string | number;
    /** Whether to show navigation controls */
    showControls?: boolean;
    /** Custom class name */
    className?: string;
    /** User's current location — renders a pulsing blue dot */
    currentLocation?: { lat: number; lng: number };
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_CENTER = {
    lat: 24.7136,
    lng: 46.6753,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function MainMap({
    lat = DEFAULT_CENTER.lat,
    lng = DEFAULT_CENTER.lng,
    zoom = 11,
    points = [],
    input = 0,
    onChange,
    height = 400,
    showControls = true,
    className = "",
    currentLocation,
}: MainMapProps) {
    const isInputMode = input > 0;
    const mapRef = useRef<MapRef>(null);

    // State for input mode markers (only used when input > 0)
    const [inputMarkers, setInputMarkers] = useState<MapPoint[]>([]);
    const initializedRef = useRef(false);

    // Delay marker rendering until after popup animation completes
    const [markersReady, setMarkersReady] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => {
            mapRef.current?.resize();
            setMarkersReady(true);
        }, 350); // After popup animation completes
        return () => clearTimeout(timer);
    }, []);

    // ViewState for map position
    const [viewState, setViewState] = useState({
        longitude: lng,
        latitude: lat,
        zoom: zoom,
    });

    // Sync inputMarkers when points prop changes (for edit mode initial load)
    useEffect(() => {
        if (isInputMode && points.length > 0 && !initializedRef.current) {
            setInputMarkers(points);
            initializedRef.current = true;
        }
    }, [isInputMode, points]);

    // Sync viewState when lat/lng/zoom props change
    useEffect(() => {
        setViewState({
            longitude: lng,
            latitude: lat,
            zoom: zoom,
        });
    }, [lat, lng, zoom]);

    // In view mode, use points directly. In input mode, use inputMarkers state.
    const markers = isInputMode ? inputMarkers : points;

    // Calculate remaining inputs
    const remainingInputs = input > 1 ? input - inputMarkers.length : 0;

    const handleMapClick = useCallback(
        (evt: any) => {
            if (input === 0) return;

            const newMarker: MapPoint = {
                lat: evt.lngLat.lat,
                lng: evt.lngLat.lng,
                color: "orange",
            };

            if (input === 1) {
                // Single marker mode - replace existing
                const newMarkers = [newMarker];
                setInputMarkers(newMarkers);
                onChange?.(newMarkers);
            } else if (inputMarkers.length < input) {
                // Multiple markers mode - add until limit
                const newMarkers = [...inputMarkers, newMarker];
                setInputMarkers(newMarkers);
                onChange?.(newMarkers);
            }
        },
        [input, inputMarkers, onChange]
    );

    const handleMarkerClick = useCallback(
        (index: number, e: React.MouseEvent) => {
            e.stopPropagation();
            if (input === 0) return;

            // Remove marker on click in input mode
            const newMarkers = inputMarkers.filter((_, i) => i !== index);
            setInputMarkers(newMarkers);
            onChange?.(newMarkers);
        },
        [input, inputMarkers, onChange]
    );

    const handleViewStateChange = useCallback((evt: ViewStateChangeEvent) => {
        setViewState(evt.viewState);
    }, []);

    const containerStyle = useMemo(
        () => ({
            height: typeof height === "number" ? `${height}px` : height,
        }),
        [height]
    );

    const hintText = useMemo(() => {
        if (input === 1) {
            return inputMarkers.length === 0 ? "انقر على الخريطة لتحديد الموقع" : "انقر على العلامة لإزالتها";
        }
        return remainingInputs > 0 ? `انقر لإضافة ${remainingInputs} نقاط أخرى` : "انقر على علامة لإزالتها";
    }, [input, inputMarkers.length, remainingInputs]);

    const handleLocate = useCallback(() => {
        if (!currentLocation) return;
        mapRef.current?.flyTo({ center: [currentLocation.lng, currentLocation.lat], zoom: 15, duration: 1400 });
    }, [currentLocation]);

    return (
        <div className={`main-map${isInputMode ? " main-map--input" : ""} ${className}`.trim()} style={containerStyle}>
            <Map
                ref={mapRef}
                {...viewState}
                onMove={handleViewStateChange}
                mapboxAccessToken={import.meta.env.VITE_MAPBOX_TOKEN}
                mapStyle={MAP_STYLE}
                onClick={handleMapClick}
                cursor={isInputMode ? "crosshair" : "grab"}
                attributionControl={false}
            >
                {showControls && <NavigationControl position="bottom-right" showCompass={false} />}

                {markersReady &&
                    markers.map((point, index) => (
                        <Marker
                            key={`marker-${index}-${point.lat}-${point.lng}`}
                            longitude={point.lng}
                            latitude={point.lat}
                            anchor="bottom"
                            onClick={(e: any) => handleMarkerClick(index, e.originalEvent as React.MouseEvent)}
                        >
                            <div className={`main-map-marker main-map-marker--${point.color || "orange"}`}>
                                <div className="main-map-marker-card">
                                    {point.icon ? (
                                        <img src={point.icon} alt="" className="main-map-marker-icon" />
                                    ) : (
                                        <div className="main-map-marker-dot" />
                                    )}
                                    {point.title && <span className="main-map-marker-title">{point.title}</span>}
                                </div>
                                <div className="main-map-marker-tip" />
                            </div>
                        </Marker>
                    ))}

                {markersReady && currentLocation && (
                    <Marker longitude={currentLocation.lng} latitude={currentLocation.lat} anchor="center">
                        <div className="main-map-location-dot">
                            <div className="main-map-location-dot-ring" />
                            <div className="main-map-location-dot-core" />
                        </div>
                    </Marker>
                )}
            </Map>

            {currentLocation && (
                <button type="button" className="main-map-locate-btn" onClick={handleLocate} aria-label="Go to my location">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 3" />
                    </svg>
                </button>
            )}

            {isInputMode && <div className="main-map-hint">{hintText}</div>}
        </div>
    );
}
